import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import { ContainerProps } from '@mui/material/Container'
import { Breakpoint } from '@mui/system'
import { ReactNode } from 'react'

import { LuneThemeNew, Text } from '../index'

import BreadCrumbs, { BreadCrumb } from './BreadCrumbs'

const { palette, spacing } = LuneThemeNew

export interface MainLayoutContainerNewProps extends ContainerProps {
    title: string
    moreInfo?: ReactNode
    subtitle?: string
    children: ReactNode
    maxWidth?: Breakpoint | false
    primaryButton?: ReactNode
    secondaryButton?: ReactNode
    sx?: SxProps
    isTestMode: boolean
    breadCrumbs?: BreadCrumb[]
}

const MainLayoutContainerNew = ({
    children,
    title,
    moreInfo,
    subtitle,
    primaryButton,
    secondaryButton,
    isTestMode,
    sx,
    breadCrumbs,
}: MainLayoutContainerNewProps) => {
    return (
        <Box
            sx={{
                width: '100%',
                margin: 'auto',
                p: `0 !important`,
                boxSizing: 'border-box',
                backgroundColor: `Surface300`,
                minHeight: `calc(100vh - ${isTestMode ? spacing(3) : spacing(0)})`,
                '*': {
                    boxSizing: 'border-box',
                },
                ...sx,
            }}
        >
            <Box
                className={'main-layout-container__header'}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 2,
                    px: 4,
                    position: 'sticky',
                    top: isTestMode ? spacing(3) : 0,
                    backgroundColor: `Surface100`,
                    zIndex: 9,
                    borderLeft: '1px solid',
                    borderColor: palette.Surface300,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        flexDirection: 'column',
                        gap: 1,
                        overflow: 'hidden',
                    }}
                >
                    {breadCrumbs && <BreadCrumbs items={breadCrumbs} />}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Text
                            variant={'h5'}
                            sx={{
                                maxWidth: '100%',
                                whiteSpace: 'break-spaces',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                wordBreak: 'break-word',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {title}
                        </Text>
                        {moreInfo}
                    </Box>
                    {subtitle && (
                        <Text
                            variant={'body3'}
                            sx={{
                                color: `TextSecondary`,
                                maxWidth: '100%',
                                whiteSpace: 'break-spaces',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                wordBreak: 'break-word',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {subtitle}
                        </Text>
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    {secondaryButton}
                    {primaryButton}
                </Box>
            </Box>
            <Box
                className={'main-layout-container__body'}
                sx={{
                    p: 3,
                }}
            >
                {children}
            </Box>
        </Box>
    )
}

export default MainLayoutContainerNew
